import { FormEvent, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  RufnummerInput,
  createRufnummer,
  deleteRufnummer,
  fetchRufnummern,
} from "@/lib/api"
import { useToast } from "@/components/Toast"
import { SkeletonZeile } from "@/components/Skeleton"
import { EmptyState } from "@/components/EmptyState"
import { Button, Input, Select } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"

const TYP_OPTIONEN: [string, string][] = [
  ["mit_durchwahl", "Mit Durchwahl"],
  ["ohne_durchwahl", "Ohne Durchwahl"],
]

const LEER: RufnummerInput = {
  laendervorwahl: "+49",
  vorwahl: "",
  stammnummer: "",
  durchwahl: "",
  label: "",
  typ: "mit_durchwahl",
}

/** Rufnummern-Verwaltung innerhalb der Organisation-Detailseite (Ninox `N`). */
export function RufnummernSection({ organisationId }: { organisationId: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [neu, setNeu] = useState<RufnummerInput>(LEER)
  const [zeigeNeu, setZeigeNeu] = useState(false)
  const [loeschenId, setLoeschenId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ["rufnummern", "organisation", organisationId],
    queryFn: () => fetchRufnummern({ organisationId }),
  })

  const invalidieren = () =>
    queryClient.invalidateQueries({ queryKey: ["rufnummern", "organisation", organisationId] })

  const anlegen = useMutation({
    mutationFn: (payload: RufnummerInput) => createRufnummer({ ...payload, organisationId }),
    onSuccess: () => {
      invalidieren()
      setNeu(LEER)
      setZeigeNeu(false)
      toast.show("Rufnummer gespeichert.", "success")
    },
    onError: () => toast.show("Rufnummer konnte nicht gespeichert werden.", "error"),
  })

  const loeschen = useMutation({
    mutationFn: (id: string) => deleteRufnummer(id),
    onSuccess: () => {
      invalidieren()
      setLoeschenId(null)
      toast.show("Rufnummer gelöscht.", "success")
    },
    onError: () => toast.show("Rufnummer konnte nicht gelöscht werden.", "error"),
  })

  function onSubmitNeu(e: FormEvent) {
    e.preventDefault()
    anlegen.mutate(neu)
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Rufnummern</h2>
        <button
          type="button"
          onClick={() => setZeigeNeu(true)}
          className="rounded-md border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          + Rufnummer hinzufügen
        </button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <SkeletonZeile className="h-10 w-full" />
          <SkeletonZeile className="h-10 w-full" />
        </div>
      )}

      <ul className="space-y-2">
        {data?.map((r) => (
          <li
            key={r.id}
            className="flex items-center justify-between rounded-md border border-slate-100 p-3 text-sm"
          >
            <div>
              <p className="font-medium text-slate-900">{r.anzeige ?? "—"}</p>
              <p className="text-slate-500">
                {r.label ? `${r.label} · ` : ""}
                {TYP_OPTIONEN.find(([v]) => v === r.typ)?.[1] ?? "—"}
              </p>
            </div>
            {loeschenId === r.id ? (
              <span className="flex items-center gap-1.5 text-xs">
                <span className="text-slate-600">Wirklich löschen?</span>
                <button
                  type="button"
                  onClick={() => loeschen.mutate(r.id)}
                  disabled={loeschen.isPending}
                  className="rounded-md bg-red-600 px-2 py-1 text-white hover:bg-red-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Ja, löschen
                </button>
                <button
                  type="button"
                  onClick={() => setLoeschenId(null)}
                  className="rounded-md border border-slate-300 px-2 py-1 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Abbrechen
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setLoeschenId(r.id)}
                className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              >
                Löschen
              </button>
            )}
          </li>
        ))}
      </ul>

      {!isLoading && (data?.length ?? 0) === 0 && !zeigeNeu && (
        <EmptyState
          text="Noch keine Rufnummern erfasst."
          aktion={{ label: "+ Rufnummer hinzufügen", onClick: () => setZeigeNeu(true) }}
        />
      )}

      <Sheet open={zeigeNeu} onOpenChange={setZeigeNeu}>
        <SheetContent sheetKey="rufnummer-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neue Rufnummer</SheetTitle>
            <SheetDescription>Rufnummer für diese Organisation erfassen.</SheetDescription>
          </SheetHeader>
          <form id="rufnummer-neu-form" onSubmit={onSubmitNeu} className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">Ländervorwahl</label>
                <Input
                  value={neu.laendervorwahl ?? ""}
                  onChange={(e) => setNeu({ ...neu, laendervorwahl: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">Vorwahl</label>
                <Input
                  value={neu.vorwahl ?? ""}
                  onChange={(e) => setNeu({ ...neu, vorwahl: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">Stammnummer</label>
                <Input
                  value={neu.stammnummer ?? ""}
                  onChange={(e) => setNeu({ ...neu, stammnummer: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">Durchwahl</label>
                <Input
                  value={neu.durchwahl ?? ""}
                  onChange={(e) => setNeu({ ...neu, durchwahl: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">
                Label (z. B. Zentrale, Mobil)
              </label>
              <Input
                value={neu.label ?? ""}
                onChange={(e) => setNeu({ ...neu, label: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Typ</label>
              <Select value={neu.typ ?? ""} onChange={(e) => setNeu({ ...neu, typ: e.target.value })}>
                {TYP_OPTIONEN.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeNeu(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="rufnummer-neu-form" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Speichert…" : "Rufnummer speichern"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  )
}
